import type { CustomPrompt, PendingSignup, UserDoc } from '@/api';
import { atom } from 'recoil';

import type { IStep } from '@chainlit/react-client';

export const chatSettingsOpenState = atom<boolean>({
  key: 'chatSettingsOpen',
  default: false
});

export type AccountExpiredPayload = {
  reason: string;
  logout_in_seconds: number;
};

export const accountExpiredState = atom<AccountExpiredPayload | null>({
  key: 'accountExpired',
  default: null
});

// Incremented when the operator completes a legal/privacy acceptance. Terminals
// watch this to reconnect after their mount-time connect was refused by the
// acceptance gate (otherwise the operator would have to click Connect manually).
export const termsAcceptedSignalState = atom<number>({
  key: 'termsAcceptedSignal',
  default: 0
});

// True once the legal + privacy gates are satisfied (or unreachable — fail open).
// App defers the chat socket connect until this is set so on_chat_start runs
// after acceptance; otherwise the backend pipeline bails on the compliance gate
// and the model only loads after a manual page refresh.
export const legalGateReadyState = atom<boolean>({
  key: 'legalGateReady',
  default: false
});

export type UserSidebarSection =
  | 'account'
  | 'response'
  | 'features'
  | 'prompts'
  | 'docs'
  | 'voice'
  | 'help'
  | 'admin';

export type AdminUserSummary = {
  id: string;
  identifier: string;
  role: 'admin' | 'user';
  disabled: boolean;
  createdAt?: string | null;
  last_login_at?: string | null;
  provider?: string | null;
  total_tokens?: number;
  total_cost?: number;
  pricing_missing?: boolean;
  is_temporary?: boolean;
  temp_expires_at?: string | null;
};

export const adminUsersState = atom<AdminUserSummary[]>({
  key: 'adminUsers',
  default: []
});

export const signupsEnabledState = atom<boolean>({
  key: 'signupsEnabled',
  default: true
});

export const adminUserDetailDialogOpenState = atom<boolean>({
  key: 'adminUserDetailDialogOpen',
  default: false
});

export const adminUserDetailIdentifierState = atom<string | null>({
  key: 'adminUserDetailIdentifier',
  default: null
});

export const auditLogDialogOpenState = atom<boolean>({
  key: 'auditLogDialogOpen',
  default: false
});

// Seeds AuditLogSection's user filter and opens it straight to the Events tab.
// Null opens the log on Overview with no filter.
export const auditLogUserIdentifierState = atom<string | null>({
  key: 'auditLogUserIdentifier',
  default: null
});

export const userSidebarOpenState = atom<boolean>({
  key: 'userSidebarOpen',
  default: false
});

export const userSidebarSectionState = atom<UserSidebarSection>({
  key: 'userSidebarSection',
  default: 'response'
});

export const customPromptsState = atom<CustomPrompt[]>({
  key: 'customPrompts',
  default: []
});

export const activeCustomPromptIdState = atom<string | null>({
  key: 'activeCustomPromptId',
  default: null
});

export const userDocsState = atom<UserDoc[]>({
  key: 'userDocs',
  default: []
});

export const userDocsFilterState = atom<string[] | null>({
  key: 'userDocsFilter',
  default: null
});

export const userDocsTenantStatusState = atom<string | null>({
  key: 'userDocsTenantStatus',
  default: null
});

export const pendingTerminalHistoriesState = atom<Record<string, string>>({
  key: 'pendingTerminalHistories',
  default: {}
});

export type ROEFormFieldGroup = {
  allowed_techniques: string[];
  denied_techniques: string[];
  allowed_tools: string[];
  denied_tools: string[];
  notes: string;
  raw_text?: string;
  allowed_target_ips?: string[];
};

export type ROEFormPreset = {
  allowed_techniques?: string[] | string;
  denied_techniques?: string[] | string;
  allowed_tools?: string[] | string;
  denied_tools?: string[] | string;
  notes?: string;
  allowed_target_ips?: string[] | string;
};

export type ROEFormPayload = {
  form_id: string;
  mode: 'initial' | 'edit';
  presets: Record<string, ROEFormPreset>;
  known_techniques: string[];
  known_tools: string[];
  current: ROEFormFieldGroup;
  default: ROEFormFieldGroup;
};

export const roeFormState = atom<ROEFormPayload | null>({
  key: 'roeFormPayload',
  default: null
});

export type ReportStandardChoice = {
  id: string;
  label: string;
};

export type ReportFormPayload = {
  form_id: string;
  standards: ReportStandardChoice[];
  formats: string[];
  current: {
    template: string;
    output_format: string;
    advanced: boolean;
    notes: string;
  };
  accepted_file_types: string[];
  max_files: number;
  max_size_mb: number;
};

export const reportFormState = atom<ReportFormPayload | null>({
  key: 'reportFormPayload',
  default: null
});

export type RewindConfirmPayload = {
  form_id: string;
  message_id: string;
  summary: string;
  mode_default: 'planning' | 'full';
  warning: string;
};

export const rewindConfirmState = atom<RewindConfirmPayload | null>({
  key: 'rewindConfirmPayload',
  default: null
});

export type PreEditSnapshot = {
  message_id: string;
  messages: IStep[];
};

export const preEditSnapshotState = atom<PreEditSnapshot | null>({
  key: 'preEditSnapshot',
  default: null
});

export type OperationSnapshotEntry = {
  message_id: string;
  turn_index: number;
  created_at: string;
  summary: string;
};

export const snapshotHistoryState = atom<OperationSnapshotEntry[]>({
  key: 'operationSnapshotHistory',
  default: []
});

export type DiscoveredHost = {
  id: string;
  ip: string;
  hostname?: string;
  os?: string;
  vendor?: string;
  mac?: string;
};

export type TargetSelectPayload = {
  form_id: string;
  hosts: DiscoveredHost[];
};

export const targetSelectState = atom<TargetSelectPayload | null>({
  key: 'targetSelectPayload',
  default: null
});

export const pendingSignupsState = atom<PendingSignup[]>({
  key: 'pendingSignups',
  default: []
});
